/**
 * The receipt's layout, as data — and the function that turns it into a `Slip`.
 *
 * This used to be a hardcoded sequence of `blocks.push(...)` in `slip-model.ts`,
 * which made «put the shop's company name under the total» a code change, a
 * deploy and a developer. The sequence is now a document an operator arranges in
 * the fleet console, stored per chain with a per-shop fork; `slip-model.ts` keeps
 * what a receipt *is*, and this file keeps what one particular chain wants on it.
 *
 * **`DEFAULT_TEMPLATE` is the design in `docs/receipt/bill.jpg`.** A machine
 * with no template row prints exactly that, and `template.test.ts` holds it as
 * a snapshot rather than an intention — the
 * order of the blocks stopped being a property of the code the moment it became
 * a property of a document.
 */
import { format, rate, sum } from '@mgc/domain'
import type { Money } from '@mgc/domain'
import { BRAND_HEADER } from './brand'
import type { Align, Slip, SlipBlock, SlipBranch, SlipImage, SlipOrder, SlipStrings } from './slip-model'
import { MN, placedAtLabel, placedOnLabel } from './slip-model'

/*
 * The model travels with the template.
 *
 * `@mgc/receipt/template` is the entry point every Node consumer takes — the
 * server, `@mgc/db`, `@mgc/protocol` — because the package barrel pulls
 * `html.ts`, which is the one file here that needs the DOM lib. Whoever imports
 * a template needs `Slip`, `SlipOrder` and `MN` as well, so they are re-exported
 * rather than sending each caller to a second specifier.
 */
export * from './slip-model'
export * from './samples'
export * from './brand'

/**
 * The pieces a slip is made of.
 *
 * Most are **generated**: they need the order and emit nothing when the order
 * has nothing to say — no discount, no company, no lottery number yet. Two are
 * the operator's own — `text` and `space` — and carry their content with them.
 */
export const SECTION_KINDS = [
  'logo',
  'branchName',
  'branchAddress',
  'orderNumber',
  'orderType',
  'seller',
  'placedAt',
  'lines',
  'discount',
  'total',
  'vat',
  'payments',
  'paymentMethod',
  'change',
  'company',
  'fiscal',
  'fiscalBillId',
  'timestamp',
  'wifi',
  'thanks',
  'text',
  'space'
] as const

/**
 * Derived from the array rather than declared beside it, so `@mgc/protocol`'s
 * `z.enum(SECTION_KINDS)` and this type cannot drift apart — a kind in one and
 * not the other is a section the console offers and the wire refuses.
 */
export type SectionKind = (typeof SECTION_KINDS)[number]

/**
 * What an operator may not switch off.
 *
 * e-Barimt registers a sale three ways and the customer picks: the QR code, the
 * ДДТД, or the lottery number. All three are on this piece of paper or they are
 * nowhere, so a template without the fiscal block is a shop whose customers
 * cannot claim their VAT and nobody finds out until a tax season. The totals go
 * with it for the same reason one step less dramatic: a receipt that does not
 * say what was charged is not a receipt.
 *
 * They stay **reorderable and relabelable**. This is a floor, not a freeze — and
 * it is enforced here, in the zod schema and in the console, because a rule that
 * only the screen knows is a rule a hand-made POST does not have to keep.
 */
export const REQUIRED_SECTIONS: readonly SectionKind[] = ['lines', 'total', 'vat', 'fiscal']

export const isRequired = (kind: SectionKind): boolean => REQUIRED_SECTIONS.includes(kind)

/**
 * One line of the arrangement.
 *
 * `text` is what the operator typed; on a generated section the same field is a
 * label override, so «Нийт дүн» can become «ДҮН» without touching the code that
 * knows where the total goes.
 *
 * `align`, `bold` and `size` are deliberately honoured **only on `text`**. The
 * three renderers already disagree about what `size: 'large'` means — raster
 * draws it at 1.6× the body, HTML the same in millimetres, ESC/POS as double-width — and letting an
 * operator pick it for the total would be choosing something that happens
 * differently on every printer in the fleet.
 */
export interface TemplateEntry {
  /** Stable across a save, so the console can key rows and the audit can name one. */
  readonly id: string
  readonly kind: SectionKind
  readonly enabled: boolean
  /** The operator's own words: a `text` block's content, or a label override. */
  readonly text?: string
  readonly align?: Align
  readonly bold?: boolean
  readonly size?: 'normal' | 'large'
  /** `space` only. */
  readonly lines?: number
}

/**
 * The logo, dithered once and carried whole.
 *
 * **Not a URL into the bucket**, which is where product pictures live (D28).
 * Three reasons, and the first is fatal: a cross-origin image taints the canvas
 * `raster.ts` draws on, so `getImageData` throws and the receipt fails — not the
 * logo, the receipt. The second is that a slip must print with the uplink down.
 * The third is that only the console knows the head is one bit deep.
 *
 * `dots` is the printable width it was dithered *for*. A renderer draws it at
 * `min(dots, target)` and never upscales: a dither resampled by a fraction of a
 * dot is mud, which is the argument `raster.ts` already makes about QR modules.
 */
export type TemplateLogo = SlipImage

/** The labels that arrived with the 2026-09 redesign. */
type LaterLabel =
  | 'sellerName'
  | 'sellerTin'
  | 'branch'
  | 'branchAddress'
  | 'date'
  | 'product'
  | 'quantity'
  | 'unitPrice'
  | 'amount'
  | 'paid'
  | 'paymentMethod'
  | 'ebarimtAmount'
  | 'wifiPassword'

/**
 * The strings a template carries: complete for everything a template has
 * always had, and optional for what the redesign added, because a document
 * saved before it is still a valid document. `buildSlip` fills the gaps.
 */
export type TemplateLabels = Omit<SlipStrings, LaterLabel> & Partial<Pick<SlipStrings, LaterLabel>>

export interface ReceiptTemplate {
  readonly entries: readonly TemplateEntry[]
  readonly logo: TemplateLogo | null
  /** `DEFAULT_TEMPLATE.labels` is `MN` itself. */
  readonly labels: TemplateLabels
}

/**
 * The arrangement in `docs/receipt/bill.jpg`, which replaced the one this system
 * shipped with in 2026-09.
 *
 * A masthead, the order number as the heading, the seller and the shop, the
 * items as a table, the totals, then the e-Barimt QR with the lottery number
 * and the registered amount beside it, and a centred footer. Sections the
 * design leaves out — the order type, the change, a company buyer — stay in the
 * document so an operator can bring them back, and the ones that only print
 * when a sale has something to say keep that behaviour.
 */
export const DEFAULT_ENTRIES: readonly TemplateEntry[] = [
  // The logo entry's own text is the right-hand column of the masthead, one
  // line per line — the chain's phone number and website.
  { id: 'logo', kind: 'logo', enabled: true, text: '7570-6565\nwww.mega-mgccoffee.mn' },
  { id: 'space-1', kind: 'space', enabled: true },
  { id: 'order-number', kind: 'orderNumber', enabled: true },
  { id: 'order-type', kind: 'orderType', enabled: false },
  { id: 'space-2', kind: 'space', enabled: true },
  { id: 'seller', kind: 'seller', enabled: true },
  { id: 'branch-name', kind: 'branchName', enabled: true },
  { id: 'branch-address', kind: 'branchAddress', enabled: true },
  // The ДДТД is one of the three ways a customer can register the sale, and
  // the design prints it with the shop's details rather than at the bottom.
  { id: 'fiscal-bill-id', kind: 'fiscalBillId', enabled: true },
  { id: 'placed-at', kind: 'placedAt', enabled: true },
  { id: 'lines', kind: 'lines', enabled: true },
  { id: 'discount', kind: 'discount', enabled: true },
  { id: 'total', kind: 'total', enabled: true },
  { id: 'payments', kind: 'payments', enabled: true },
  { id: 'vat', kind: 'vat', enabled: true },
  { id: 'payment-method', kind: 'paymentMethod', enabled: true },
  { id: 'change', kind: 'change', enabled: true },
  { id: 'company', kind: 'company', enabled: true },
  { id: 'fiscal', kind: 'fiscal', enabled: true },
  { id: 'space-3', kind: 'space', enabled: true },
  { id: 'timestamp', kind: 'timestamp', enabled: true },
  { id: 'wifi', kind: 'wifi', enabled: true },
  { id: 'thanks', kind: 'thanks', enabled: true },
  { id: 'space-4', kind: 'space', enabled: true, lines: 2 }
]

export const DEFAULT_TEMPLATE: ReceiptTemplate = {
  entries: DEFAULT_ENTRIES,
  logo: BRAND_HEADER,
  labels: MN
}

/**
 * The number the customer is given, on the slip and on the success screen.
 *
 * 1C's check number when the filing has landed, so the slip and 1C's books
 * carry the same number; ours when it has not — offline, queued, refused —
 * because a customer still needs a number to be called by. One function so
 * the screen and the paper cannot disagree.
 */
export function orderNumberOf(order: Pick<SlipOrder, 'displayNumber' | 'fiscal'>): string {
  const onec = order.fiscal?.onecNumber
  return onec === undefined || onec === null ? order.displayNumber : String(onec)
}

/**
 * Build the slip.
 *
 * The order number is first and large in the default arrangement, because the
 * one thing the customer needs off this piece of paper is the number the barista
 * will call — but that is now the *template's* opinion rather than this
 * function's, which is the whole point of the file.
 */
export function buildSlip(order: SlipOrder, branch: SlipBranch, template: ReceiptTemplate = DEFAULT_TEMPLATE): Slip {
  return buildSlipWithOrigins(order, branch, template).slip
}

/**
 * Which entry typed which blocks: a half-open range into `slip.blocks`.
 *
 * `from === to` is the ordinary case rather than a fault — a discount section
 * on a sale that had none, a company section on a walk-in — and saying so is
 * the point: it is how the console can tell an operator *«this prints nothing
 * for this sale»* instead of leaving them to hunt the paper for a line that was
 * never going to be there.
 */
export interface SlipOrigin {
  readonly id: string
  readonly from: number
  readonly to: number
}

/**
 * {@link buildSlip}, with the provenance kept.
 *
 * The ranges are recorded here rather than derived afterwards because only this
 * loop knows them: `emit` appends, and the blocks it appends carry nothing that
 * says which entry asked for them. Two functions rather than one optional
 * output, so every caller that only wants a slip — the kiosk, the three
 * renderers, the tests — keeps taking a `Slip` and nothing else.
 */
export function buildSlipWithOrigins(
  order: SlipOrder,
  branch: SlipBranch,
  template: ReceiptTemplate = DEFAULT_TEMPLATE
): { slip: Slip; origins: readonly SlipOrigin[] } {
  const blocks: SlipBlock[] = []
  const origins: SlipOrigin[] = []
  // A template saved before the redesign has none of its labels; the
  // catalogue's own words fill the gap rather than an «undefined» on paper.
  const labels: SlipStrings = { ...MN, ...template.labels }

  for (const entry of template.entries) {
    // A required section prints whether or not the document says so. The zod
    // schema refuses a document that disables one, but a kiosk may be holding a
    // cached document written before this rule existed, and the receipt in that
    // customer's hand is not the place to discover it.
    if (!entry.enabled && !isRequired(entry.kind)) continue
    const from = blocks.length
    emit(entry, blocks, order, branch, labels, template)
    origins.push({ id: entry.id, from, to: blocks.length })
  }

  return { slip: { blocks, cut: true }, origins }
}

/** The label an entry wants, or the one the catalogue of strings supplies. */
const labelOf = (entry: TemplateEntry, fallback: string): string =>
  entry.text !== undefined && entry.text.length > 0 ? entry.text : fallback

function emit(
  entry: TemplateEntry,
  blocks: SlipBlock[],
  order: SlipOrder,
  branch: SlipBranch,
  labels: SlipStrings,
  template: ReceiptTemplate
): void {
  const money = (m: Money) => format(m, { symbol: false })

  switch (entry.kind) {
    case 'logo': {
      const lines = (entry.text ?? '')
        .split('\n')
        .map((line) => line.trim())
        .filter((line) => line.length > 0)
      if (template.logo !== null || lines.length > 0) blocks.push({ kind: 'header', image: template.logo, lines })
      return
    }

    case 'branchName':
      blocks.push({ kind: 'text', text: `${labelOf(entry, labels.branch)}: ${branch.name}` })
      return

    case 'branchAddress':
      if (branch.address !== null) {
        blocks.push({ kind: 'text', text: `${labelOf(entry, labels.branchAddress)}: ${branch.address}` })
      }
      return

    case 'orderNumber':
      blocks.push({ kind: 'number', label: labelOf(entry, labels.orderNumber), value: orderNumberOf(order) })
      return

    case 'orderType':
      blocks.push({
        kind: 'text',
        text: order.orderType === 'eat_in' ? labels.eatIn : labels.takeOut,
        align: 'center',
        bold: true
      })
      return

    case 'seller':
      // The label override is the seller's name, which is what an operator
      // renaming this section means; the number stays the catalogue's.
      blocks.push({ kind: 'row', left: labelOf(entry, labels.sellerName), right: labels.sellerTin })
      return

    case 'placedAt':
      blocks.push({ kind: 'text', text: `${labelOf(entry, labels.date)}: ${placedOnLabel(order.placedAt)}` })
      return

    case 'lines': {
      blocks.push({ kind: 'rule' })
      blocks.push({
        kind: 'columns',
        cells: [labels.product, labels.quantity, labels.unitPrice, labels.amount],
        head: true
      })
      blocks.push({ kind: 'rule' })
      for (const line of order.lines) {
        // The line's total already carries its add-ons, so the unit price is
        // that divided back out, and the add-ons below it carry no amount —
        // printing theirs as well would make the Дүн column add up to more
        // than the customer paid.
        const unit = line.quantity > 0 ? rate(line.total, 1, line.quantity, 'half-up') : line.total
        blocks.push({
          kind: 'columns',
          cells: [line.name, String(line.quantity), money(unit), money(line.total)]
        })
        for (const addOn of line.addOns) {
          blocks.push({
            kind: 'text',
            text: addOn.quantity > 1 ? `  + ${addOn.label} ×${addOn.quantity}` : `  + ${addOn.label}`,
            size: 'small'
          })
        }
        if (line.note !== null && line.note.length > 0) {
          blocks.push({ kind: 'text', text: `  · ${line.note}`, size: 'small' })
        }
      }
      blocks.push({ kind: 'rule' })
      return
    }

    case 'discount':
      if (order.savings > 0n) {
        blocks.push({ kind: 'row', left: labelOf(entry, labels.discount), right: `-${money(order.savings)}` })
      }
      return

    case 'total':
      blocks.push({ kind: 'row', left: labelOf(entry, labels.total), right: money(order.gross) })
      return

    case 'vat':
      blocks.push({ kind: 'row', left: labelOf(entry, labels.vat), right: money(order.vat) })
      return

    case 'payments':
      // One amount, however many payments made it: the design has a single
      // «Төлбөрийн дүн», and the methods are the next section's.
      if (order.payments.length > 0) {
        blocks.push({
          kind: 'row',
          left: labelOf(entry, labels.paid),
          right: money(sum(order.payments.map((payment) => payment.amount)))
        })
      }
      return

    case 'paymentMethod': {
      const methods = [...new Set(order.payments.map((payment) => labels.paidBy[payment.method] ?? payment.method))]
      if (methods.length > 0) {
        blocks.push({ kind: 'row', left: labelOf(entry, labels.paymentMethod), right: methods.join(', ') })
      }
      return
    }

    case 'change':
      if (order.change > 0n) {
        blocks.push({ kind: 'row', left: labelOf(entry, labels.change), right: money(order.change) })
      }
      return

    case 'company':
      if (order.company !== null) {
        blocks.push({ kind: 'row', left: labelOf(entry, labels.company), right: order.company.registrationNumber })
        blocks.push({ kind: 'text', text: order.company.name })
      }
      return

    case 'fiscal':
      blocks.push({ kind: 'rule' })
      if (order.fiscal?.lottery != null) {
        blocks.push({
          kind: 'fiscalPanel',
          qr: order.fiscal.qrData,
          items: [
            { label: labelOf(entry, labels.lottery), value: order.fiscal.lottery },
            { label: labels.ebarimtAmount, value: money(order.gross) }
          ]
        })
      } else {
        blocks.push({ kind: 'text', text: labels.fiscalPending, align: 'center' })
      }
      return

    case 'fiscalBillId':
      if (order.fiscal?.billId != null) {
        blocks.push({ kind: 'text', text: `${labelOf(entry, labels.billId)}: ${order.fiscal.billId}` })
      }
      return

    case 'timestamp':
      blocks.push({ kind: 'text', text: placedAtLabel(order.placedAt), align: 'center' })
      return

    case 'wifi':
      if (branch.wifiName !== null) {
        const name = `${labelOf(entry, labels.wifi)}: ${branch.wifiName}`
        blocks.push({
          kind: 'text',
          text: branch.wifiPassword === null ? name : `${name}    ${labels.wifiPassword}: ${branch.wifiPassword}`,
          align: 'center'
        })
      }
      return

    case 'thanks':
      blocks.push({ kind: 'text', text: labelOf(entry, labels.thanks), align: 'center' })
      return

    case 'text':
      if (entry.text !== undefined && entry.text.length > 0) {
        blocks.push({
          kind: 'text',
          text: entry.text,
          align: entry.align ?? 'left',
          bold: entry.bold,
          size: entry.size
        })
      }
      return

    case 'space':
      blocks.push({ kind: 'space', lines: entry.lines })
      return

    default: {
      // The union is closed, and this is what makes adding a section kind a
      // compile error rather than a section that silently prints nothing.
      const unhandled: never = entry.kind
      void unhandled
    }
  }
}
